import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { searchLocks } from 'api/locks/endpoints/searchLocks';

import { EditReference } from './EditReference';
import { formatLockPath, lockLabel, parseLockPath, type LockReference } from './lockPath';
import type { ReferenceSource } from './referenceSource';

interface Props {
  // The stored `zone_name|name` lock path, or null for "no lock".
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  /** Floating label. Omit to render a bare control (the caller supplies its own label), as `EditString` does. */
  readonly label?: string;
  /** Text shown while nothing is selected. Say what empty means, rather than restating the label. */
  readonly placeholder?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Restrict the searchable set to one zone (server-side `zone_name` equals). Undefined widens to
  // every lock the user may list.
  readonly zoneName?: string;
}

/**
 * Searchable dropdown for locks, keyed on the `zone_name|name` path every lock consumer stores.
 *
 * `zoneName` scopes the *search*, never the display: a value already stored outside that zone still
 * renders as itself, because the item is built by splitting the path rather than by looking the key
 * up among the fetched rows. A lock the user may not list reads the same way. Both would otherwise
 * show as an empty field on an object that does hold a lock, and be overwritten by the next edit.
 *
 * Backed by server-side search, so the option list is not bounded by what fits in one eager fetch —
 * the searchable counterpart to {@link EditLockSubset}, which holds the whole set for multi-select.
 * Two-column row layout (name + zone, muted right-aligned as informational context); the server
 * matches on name only, zone is shown so users can disambiguate visually.
 */
export const EditLockReference = ({ value, onChange, label, placeholder, mandatory, disabled, zoneName }: Props) => {
  const selected = useMemo<LockReference | null>(() => (value === null || value === '' ? null : parseLockPath(value)), [value]);

  // Re-created when the scope changes so the model re-fetches; stable otherwise (a fresh closure
  // would rebuild the model every render).
  const source = useMemo<ReferenceSource<LockReference>>(
    () => ({
      kind: 'search',
      search: async (query, limit) => {
        const { items, total } = await searchLocks({ name: query || undefined, limit, zone_name: zoneName });
        return { items: items.map((lock) => ({ zoneName: lock.zone_name, name: lock.name })), total };
      },
      // Required by the source contract, which cannot know whether a consumer ever holds a key-only
      // value — but neither runs here: this key carries the whole display value, so the item the
      // control is handed is already labelled and there is nothing to hydrate. Built from the key
      // anyway, since that is both the cheapest correct answer and the only one available offline.
      resolve: async (key) => parseLockPath(String(key)),
      interimValue: (key) => parseLockPath(String(key)),
    }),
    [zoneName]
  );

  return (
    <EditReference<LockReference>
      label={label}
      placeholder={placeholder}
      value={selected}
      onChange={(lock) => onChange(lock !== null ? formatLockPath(lock) : null)}
      source={source}
      getKey={formatLockPath}
      getLabel={lockLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(lock, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{lock.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{lock.zoneName}</span>
        </div>
      )}
    />
  );
};
